import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../ui/Toast'

interface Props {
  open: boolean
  onClose: () => void
  onCreated: (site: { org_id: string; name: string; group_name?: string }) => Promise<void>
}

export function AddSiteDialog({ open, onClose, onCreated }: Props) {
  const { profile } = useAuth()
  const { toast } = useToast()

  const [name, setName] = useState('')
  const [group, setGroup] = useState('')
  const [saving, setSaving] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!profile?.org_id) return

    setSaving(true)
    try {
      await onCreated({
        org_id: profile.org_id,
        name: name.trim(),
        group_name: group.trim() || undefined,
      })
      toast('Site created', 'success')
      setName('')
      setGroup('')
      onClose()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to create site', 'error')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'

  return (
    <Modal open={open} onClose={onClose} title="Add Site">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
            Site Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g. Acme Warehouse"
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
            Group
          </label>
          <input
            type="text"
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            placeholder="Optional — for organizing sites"
            className={inputCls}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Site'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
